/**
 * The notification bell and inbox — data access (CLAUDE.md §6, §7).
 *
 * Everything here is a thin wrapper over the generated typed client. It exists
 * for the reasons playbook rule 27 was written for: one place that knows the
 * shape of `ListNotificationsQuery`, one place that decides the page sizes, and
 * nowhere at all that transcribes a request body by hand.
 *
 * Three properties of the endpoint decide the whole module's shape:
 *
 *  1. **It is always the caller's own list.** `internal/notifications/handler.go`
 *     resolves the user from the JWT (`callerID`) and has no path that accepts a
 *     user id, so there is no "view as" here and nothing to leak. That is also
 *     why one permission covers all five routes.
 *  2. **The only server-side filter is `unreadOnly`.** There is no kind,
 *     severity or date filter on the wire, so this module does not offer one:
 *     a filter applied to the current page only would silently lie about
 *     everything past the cursor.
 *  3. **`link` is an in-app path, written by the module that raised the row.**
 *     It is followed only when it is a same-origin absolute path — see
 *     `deepLink`. A notification is not a place to accept a redirect target.
 */

import {
  notificationsDelete,
  notificationsList,
  notificationsMarkAllRead,
  notificationsMarkRead,
  notificationsUnreadCount,
  type ListNotificationsQuery,
  type NotificationDTO,
  type PageNotificationDTO,
} from '@erpx/api'
import type { Tone } from '@erpx/ui'

import { api } from '@/api/client'

export type { NotificationDTO, PageNotificationDTO }

/**
 * Query keys.
 *
 * `all` is the invalidation root: marking one row read changes both the page
 * being looked at and the badge, and a bell that disagrees with the list it
 * opens is worse than no bell.
 */
export const NOTIFICATION_KEYS = {
  all: ['notifications'] as const,
  list: ['notifications', 'list'] as const,
  listPage: (params: Record<string, unknown>) => ['notifications', 'list', params] as const,
  unreadCount: ['notifications', 'unreadCount'] as const,
} as const

/** Rows in the bell panel — a glance, not a history. The inbox holds the rest. */
export const BELL_PAGE_SIZE = 8

/** Rows per inbox page. §5 caps every list endpoint at 200. */
export const INBOX_PAGE_SIZE = 50

/**
 * How often the badge re-asks. 60s is a compromise between "the approval you
 * were told about by email is already in the bell" and not putting a request
 * per user per second on `/notifications/unread-count`; the query is the
 * cheapest one in the app (a COUNT on a partial index) but it is also the only
 * one every signed-in session runs forever.
 */
export const UNREAD_POLL_MS = 60_000

export interface NotificationPageRequest {
  unreadOnly: boolean
  cursor?: string
  limit?: number
}

export function listNotifications(request: NotificationPageRequest): Promise<PageNotificationDTO> {
  // Built key by key: under `exactOptionalPropertyTypes` an explicit
  // `undefined` is not the same as an absent optional property.
  const query: ListNotificationsQuery = {
    unreadOnly: request.unreadOnly,
    limit: request.limit ?? INBOX_PAGE_SIZE,
  }
  if (request.cursor !== undefined && request.cursor !== '') query.cursor = request.cursor
  return notificationsList(api(), query)
}

export async function unreadCount(): Promise<number> {
  const result = await notificationsUnreadCount(api())
  return result.count
}

export function markRead(id: number): Promise<void> {
  return notificationsMarkRead(api(), { id: String(id) })
}

export function markAllRead(): Promise<void> {
  return notificationsMarkAllRead(api())
}

/**
 * Delete one notification.
 *
 * `DELETE /notifications/:id` is what the bell's "dismiss" means: the row is
 * the *delivery*, not the thing it is about. Deleting the notification that an
 * approval is waiting removes the reminder and leaves the approval exactly
 * where it was — which is why the inbox labels this "Dismiss" and not
 * "Delete", and why nothing here treats it as destructive to a document.
 */
export function dismiss(id: number): Promise<void> {
  return notificationsDelete(api(), { id: String(id) })
}

/** Unread means the server has not stamped `readAt`. */
export function isUnread(item: Pick<NotificationDTO, 'readAt'>): boolean {
  return item.readAt === null || item.readAt === undefined || item.readAt === ''
}

/**
 * The row's tone.
 *
 * `severity` is closed at the database layer (`notif_severity_check`) and the
 * Go `Severity` enum matches it exactly, so the four cases below are the whole
 * set. Anything else is a row written by something that bypassed the service,
 * and it renders neutral rather than throwing away a message a user needs.
 */
export function severityTone(severity: string): Tone {
  switch (severity) {
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    case 'danger':
      return 'danger'
    case 'info':
      return 'info'
    default:
      return 'neutral'
  }
}

/**
 * The in-app path this notification points at, or `''` when it points nowhere.
 *
 * Only a single-slash absolute path is followed. `//evil.example` is a
 * protocol-relative URL that a browser resolves to another origin, and a row
 * written by a future module (or replayed from an outbox) is not a source this
 * app should navigate on trust.
 */
export function deepLink(item: Pick<NotificationDTO, 'link'>): string {
  const link = item.link ?? ''
  if (!link.startsWith('/') || link.startsWith('//')) return ''
  return link
}
